import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Alert, Box, Button, CircularProgress, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from "@mui/material";
import { t, Trans } from "@lingui/macro";
import { DateTime } from "luxon";
import { CustomersService } from "../../services/customers.service";
import { Customer, LegalNature, VerificationType } from "../models";
import { VerificationHistoryEntry } from "./models";
import NavigationBack from "../../components/navigation/navigation-back/navigation-back";

/** Formats retained timestamps without inventing dates for incomplete historical records. */
function historyDate(value: string | null): string {
    if (!value) return "—";
    const date = DateTime.fromISO(value);
    return date.isValid ? date.toFormat("dd/MM/yyyy HH:mm") : "—";
}

/** Translates the recorded verification type independently of the customer's current type. */
function verificationTypeLabel(value: VerificationType | null): string {
    switch (value) {
        case VerificationType.NORMAL: return t({ id: "vh-normal", message: "Ordinaria" });
        case VerificationType.SIMPLIFIED: return t({ id: "vh-simplified", message: "Semplificata" });
        case VerificationType.REINFORCED: return t({ id: "vh-reinforced", message: "Rafforzata" });
        default: return "—";
    }
}

/** Shows verification decisions with retry, legacy context, and the shared back arrow to the customer list. */
export default function VerificationHistory() {
    const { id } = useParams<{ id: string }>();
    const [customer, setCustomer] = useState<Customer | null>(null);
    const [rows, setRows] = useState<VerificationHistoryEntry[] | null>(null);
    const [error, setError] = useState(false);
    const [attempt, setAttempt] = useState(0);

    useEffect(() => {
        let active = true;
        setCustomer(null);
        setRows(null);
        setError(false);
        CustomersService.getCustomer(id, (response: any) => {
            if (!active) return;
            if (response.hasError() || !response.data) setError(true);
            else setCustomer(response.data);
        }).catch(() => { if (active) setError(true); });
        CustomersService.getVerificationHistory(id).then(response => {
            if (!active) return;
            if (response.hasError() || !response.data) setError(true);
            else setRows(response.data.list);
        }).catch(() => { if (active) setError(true); });
        return () => { active = false; };
    }, [id, attempt]);

    const personal = customer && (customer.legalNature === LegalNature.PF || customer.legalNature === LegalNature.PP);
    const name = personal && customer?.name?.trim() && customer?.surname?.trim()
        ? `${customer.name} ${customer.surname}` : customer?.denomination || `${customer?.name || ""} ${customer?.surname || ""}`.trim();

    return <Box className="main-container">
        <NavigationBack backRoute="/customers" />
        <Typography variant="h3"><Trans id="vh-title">Storico verifiche</Trans></Typography>
        <Typography variant="h5" sx={{ mb: 3 }}>{name}</Typography>
        {error ? <Alert severity="error" action={<Button onClick={() => setAttempt(value => value + 1)}>
            <Trans id="vh-retry">Riprova</Trans>
        </Button>}><Trans id="vh-error">Impossibile caricare lo storico verifiche.</Trans></Alert>
            : (!customer || !rows) ? <CircularProgress aria-label={t({ id: "vh-loading", message: "Caricamento storico verifiche" })} />
            : rows.length === 0 ? <Alert severity="info"><Trans id="vh-empty">Non sono presenti verifiche nello storico.</Trans></Alert>
            : <>
                {rows.some(row => row.legacy) && <Alert severity="info" sx={{ mb: 2 }}>
                    <Trans id="vh-legacy-notice">I record storici importati sono parziali. I dati non disponibili sono indicati con “—”; le verifiche precedenti potrebbero non essere disponibili.</Trans>
                </Alert>}
                <TableContainer>
                    <Table aria-label={t({ id: "vh-title", message: "Storico verifiche" })}>
                        <TableHead><TableRow>
                            <TableCell><Trans id="vh-start">Inizio identificazione</Trans></TableCell>
                            <TableCell><Trans id="vh-completion">Completamento identificazione</Trans></TableCell>
                            <TableCell><Trans id="vh-decision">Data decisione</Trans></TableCell>
                            <TableCell><Trans id="vh-outcome">Esito</Trans></TableCell>
                            <TableCell><Trans id="vh-type">Tipologia verifica</Trans></TableCell>
                            <TableCell><Trans id="vh-expiry">Scadenza</Trans></TableCell>
                            <TableCell><Trans id="vh-verifier">Professionista verificatore</Trans></TableCell>
                        </TableRow></TableHead>
                        <TableBody>{rows.map(row => <TableRow key={row.id}>
                            <TableCell>{historyDate(row.identificationStartedAt)}</TableCell>
                            <TableCell>{historyDate(row.identificationCompletedAt)}</TableCell>
                            <TableCell>{historyDate(row.decidedAt)}{row.legacy && <Typography variant="caption" display="block">
                                <Trans id="vh-legacy">Record storico parziale</Trans>
                            </Typography>}</TableCell>
                            <TableCell>{row.successful ? <Trans id="vh-passed">Verificato</Trans> : <Trans id="vh-failed">Verifica fallita</Trans>}</TableCell>
                            <TableCell>{verificationTypeLabel(row.verificationType)}</TableCell>
                            <TableCell>{historyDate(row.expiresAt)}</TableCell>
                            <TableCell>{row.verifierName || "—"}</TableCell>
                        </TableRow>)}</TableBody>
                    </Table>
                </TableContainer>
            </>}
    </Box>;
}
